import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Box } from '@mui/material';
import { StartCourseOnDesktop } from '../components/Course/StartCourseOnDesktop';
import { useIsOnMobile } from '../hooks/layoutHooks';
import { StartCourseOnMobile } from '../components/Course/StartCourseOnMobile';
import { useSelector } from 'react-redux';
import { getUserInfos } from '../redux-store/authSlice/authSlice';
import { getLngsltd } from '../utils/langData.utils';
import {
  getCourseStepHtmlContent,
  getCourseSteps,
  getCourseStepsProgressForUser_Safe,
  getCourseStepVideoContent,
  getCourseStepImagesContent,
  updateUserProgressOnCourse,
  getBgImageContentUrl
} from '../services/courseDetails.service';
import { StartCourseSharedData } from '../components/Course/SharedStuff';
import axiosInstance from '../axios.config';

const StartCoursePage = () => {
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const userDetails = useSelector((state) => getUserInfos(state))
  const userId = userDetails.userID;
  const lngsltd = getLngsltd();
  const [isQuizPopupOpen, setIsQuizPopupOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [coursesSteps, setCoursesSteps] = useState([]);
  const [stepsTitles, setStepsTitles] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [imageUrls, setImageUrls] = useState([]);
  const [bgImgUrl, setBgImgUrl] = useState("");
  const [htmlContent, setHtmlContent] = useState('');
  const [videoWatched, setVideoWatched] = useState(false);
  const scrollableDivRef = useRef(null);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    fetchCourseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchCourseDetails = async () => {
    try {
      const courseSteps = await getCourseSteps(courseId)
      if (!courseSteps.data.length) return;
      const sortedCourseSteps = courseSteps.data.slice().sort((a, b) => a.stepNo - b.stepNo);

      // set course steps titles 
      setStepsTitles(sortedCourseSteps.map(x => x.stepTitle))

      const userSteps = await getCourseStepsProgressForUser_Safe(sortedCourseSteps, courseId, userId);
      const completed = userSteps.filter(step => step.status === "Completed").map(step => step.stepNumber);
      setCompletedSteps(completed);
      const firstIncompleteStep = userSteps.find(step => step.status !== 'Completed') || userSteps[0];

      const startIndex = firstIncompleteStep ? firstIncompleteStep.stepNumber - 1 : 0;

      setCurrentStep(startIndex);
      setCoursesSteps(sortedCourseSteps);

      await updateContentForNewStep(sortedCourseSteps[startIndex]);

    } catch (error) {
      console.error("error", error)
    }
  }

  const updateContentForNewStep = async (stepData) => {
    if (!stepData) return;

    // Reset content
    setVideoUrl('');
    setImageUrls([]);
    setHtmlContent('');
    setBgImgUrl('');

    const promises = [];
    promises.push(getCourseStepHtmlContent(stepData));

    if (stepData.contentType === 'Video') {
      promises.push(getCourseStepVideoContent(stepData));
    }

    if (stepData.contentType === 'Image') {
      promises.push(getCourseStepImagesContent(stepData));
    }

    promises.push(getBgImageContentUrl(stepData));

    // Await parallel responses
    const results = await Promise.all(promises);

    results.forEach((result) => {
      if (result.type === 'html') {
        setHtmlContent(result.content);
      } else if (result.type === 'video') {
        setVideoUrl(result.content);
      } else if (result.type === 'images') {
        setImageUrls(result.content);
      } else if (result.type === 'bgImg') {
        setBgImgUrl(result.content);
      }
    });
  }

  const handlePrevious = async () => {
    StartCourseSharedData.setSlideTo("right");
    try {
      const stepIndex = Math.max(currentStep - 1, 0);
      setCurrentStep(stepIndex);

      const stepData = coursesSteps[stepIndex];
      await updateContentForNewStep(stepData);

      if (scrollableDivRef.current) {
        setTimeout(() => {
          scrollableDivRef.current.parentNode.scrollTo({
            top: 10,
            behavior: 'smooth'
          });
        }, 100);
      }
    } catch (error) {
      console.error('Error navigating to previous step:', error);
    }
  };

  const handleNext = async () => {
    StartCourseSharedData.setSlideTo("left");

    const currentStepData = coursesSteps[currentStep];

    if (!currentStepData) return;

    const alreadyCompleted = completedSteps.includes(
      currentStepData.stepNo
    );

    if (
      currentStepData?.contentType === "Video" &&
      !alreadyCompleted &&
      !videoWatched
    ) {
      toast.info(
        lngsltd["Please complete the video before proceeding to the next step"]
      );
      return;
    }

    try {
      const stepCourseId = currentStepData.courseID;
      const stepNo = currentStepData.stepNo;
      await updateUserProgressOnCourse(stepCourseId, userId, stepNo);

      setCompletedSteps(prev =>
        prev.includes(stepNo)
          ? prev
          : [...prev, stepNo]
      );

      const nextStepIndex = Math.min(currentStep + 1, coursesSteps.length - 1);
      setCurrentStep(nextStepIndex);
      setVideoWatched(false);

      const stepData = coursesSteps[nextStepIndex];
      await updateContentForNewStep(stepData);

      if (scrollableDivRef.current) {
        setTimeout(() => {
          scrollableDivRef.current.parentNode.scrollTo({
            top: 10,
            behavior: 'smooth'
          });
        }, 100);
      }

    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleSubmission = async () => {
    const currentStepData = coursesSteps[currentStep];

    if (!currentStepData) return;

    const alreadyCompleted = completedSteps.includes(
      currentStepData.stepNo
    );

    if (
      currentStepData?.contentType === "Video" &&
      !alreadyCompleted &&
      !videoWatched
    ) {
      toast.info(
        lngsltd["Please complete the video before proceeding to the next step"]
      );
      return;
    }


    const stepCourseId = currentStepData.courseID;
    const stepNo = currentStepData.stepNo;

    try {

      await updateUserProgressOnCourse(stepCourseId, userId, stepNo);

      setCompletedSteps(prev =>
        prev.includes(stepNo)
          ? prev
          : [...prev, stepNo]
      );

      // Check if a quiz exists for this course
      try {
        const response = await axiosInstance.get(`/Quiz/ByCourse/${stepCourseId}`);
        const quizData = response?.data;

        const hasQuiz = Array.isArray(quizData)
          ? quizData.length > 0
          : !!quizData;

        if (hasQuiz) {
          setTimeout(() => {
            setIsQuizPopupOpen(true);
          }, 1000);
        } else {
          toast.info(lngsltd["No quiz available for this course."]);
          setTimeout(() => {
            navigate("/courses");
          }, 1500);
        }
      } catch (quizError) {
        console.error("Error checking quiz:", quizError);
        toast.error("Could not check quiz. Please try again later.");
        navigate("/courses");
      }
    } catch (error) {
      toast.error(lngsltd['Failed to submit course']);
    }
  };


  const handleQuizPopupClose = () => {
    setIsQuizPopupOpen(false);
    navigate(`/startquiz/${courseId}`);
  };

  return (
    <>
      <StartCourseInnerContent
        scrollableDivRef={scrollableDivRef}
        coursesSteps={coursesSteps}
        currentStep={currentStep}
        stepsTitles={stepsTitles}
        handlePrevious={handlePrevious}
        imageUrls={imageUrls}
        videoUrl={videoUrl}
        setVideoWatched={setVideoWatched}
        lngsltd={lngsltd}
        handleNext={handleNext}
        handleSubmission={handleSubmission}
        htmlContent={htmlContent}
        bgImgUrl={bgImgUrl}
      />

      <ToastContainer
        position="top-center"
        autoClose={1200}
        newestOnTop={false}
        closeOnClick
        progressClassName='custom-toast-progress-bar'
      />

      <QuizPopup isQuizPopupOpen={isQuizPopupOpen} handleQuizPopupClose={handleQuizPopupClose} lngsltd={lngsltd} />
    </>
  )

}

const StartCourseInnerContent = (props) => {
  const isOnMobile = useIsOnMobile()
  return (
    <>
      {isOnMobile ? <StartCourseOnMobile {...props} /> : <StartCourseOnDesktop {...props} />}
    </>
  )
}

const QuizPopup = ({ isQuizPopupOpen, handleQuizPopupClose, lngsltd }) => {
  return (
    <Dialog
      open={isQuizPopupOpen}
      onClose={null}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1.75rem',
          fontWeight: 700,
          color: '#dc3545',
          textAlign: 'center',
          pt: 4,
          pb: 2,
          background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.02) 100%)'
        }}
      >
        {lngsltd["Let's Test Your Knowledge!"]}
      </DialogTitle>
      <DialogContent sx={{ px: 4, py: 3 }}>
        <DialogContentText
          sx={{
            fontSize: '1.05rem',
            color: '#6c757d',
            lineHeight: 1.7,
            textAlign: 'center',
            mb: 1
          }}
        >
          {/* {lngsltd['The passing score for this assessment is 80%. You are allowed a maximum of 3 attempts to achieve this score.']} */}
          {lngsltd['The passing score for this assessment is 90%.']}
        </DialogContentText>
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            backgroundColor: 'rgba(220, 53, 69, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(220, 53, 69, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#dc3545'
            }}
          >
            90%
          </Box>
          <Box
            sx={{
              fontSize: '0.9rem',
              color: '#6c757d',
              fontWeight: 500
            }}
          >
            {lngsltd['Passing Score'] || 'Passing Score'}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          justifyContent: 'center'
        }}
      >
        <Button
          onClick={handleQuizPopupClose}
          variant="contained"
          sx={{
            backgroundColor: '#10b981',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '1.1rem',
            px: 5,
            py: 1.5,
            borderRadius: '10px',
            textTransform: 'none',
            minWidth: '180px',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#059669',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)'
            }
          }}
        >
          {lngsltd['Start']}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


export default StartCoursePage;
